/**
 * NotReadyPage.tsx — /:clinicSlug/not-ready, "what's holding you back?" (T-2).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey.
 *
 * Reached ONLY from the plan page's quiet "second thoughts" line, in a NEW TAB,
 * by an approved patient who has not paid. One tap on a reason, one Send.
 *
 * ⚠️ GTM NEVER LOADS HERE, and that is why this is a page and not an inline
 * panel on the plan step. The plan step is `/:clinicSlug`, a tracked path; a
 * panel rendering "I'm worried about side effects" there would put a
 * health-adjacent sentence into a DOM that a tag manager administered outside
 * this estate can read by click text. `isTrackedPath` admits only one-segment
 * paths and `/:clinicSlug/callback`, so this two-segment path is untracked —
 * and gtm.spec pins it, because an allow-list that nobody asserts is one edit
 * away from quietly including it.
 *
 * THE ID ARRIVES IN THE FRAGMENT and is scrubbed from the address bar on
 * arrival (see dropOffReasons.ts for why not the query, and why not state).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { recordDropOffReason } from '../api/journey';
import { Alert, Card, Help, PageFrame, Title } from '../ui/kit';
import { stripKeyFromLocation } from './urlKeys';
import {
  DROP_OFF_HANDOFF_KEY,
  DROP_OFF_REASON_OPTIONS,
  PEOPLE_ROUTED_REASONS,
  readDropOffAssessmentId,
  type DropOffReasonKey,
} from './dropOffReasons';
import { JourneyButton } from './JourneyUi';

type SendState = 'idle' | 'sending' | 'sent' | 'error';

export function NotReadyPage() {
  const { clinicSlug = 'weightloss' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Read ONCE at mount: the scrub below rewrites the address bar, and a re-read
  // after it would find nothing.
  const [assessmentId] = useState<string | null>(() => readDropOffAssessmentId(location.hash));
  const [reason, setReason] = useState<DropOffReasonKey | null>(null);
  const [state, setState] = useState<SendState>('idle');

  useEffect(() => {
    if (!location.hash) return;
    const { search, hash } = stripKeyFromLocation(location, DROP_OFF_HANDOFF_KEY);
    navigate({ pathname: location.pathname, search, hash }, { replace: true });
    // Once, on arrival — the id is already frozen in state above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function send() {
    if (!assessmentId || !reason || state === 'sending') return;
    setState('sending');
    try {
      await recordDropOffReason(assessmentId, reason);
      setState('sent');
    } catch {
      setState('error');
    }
  }

  if (!assessmentId) {
    // Honest about why there is nothing to answer, rather than a form that
    // would post into the void.
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>What's holding you back?</Title>
        <Help>This page opens from your plan. Go back to the email we sent you and use the link there.</Help>
      </PageFrame>
    );
  }

  if (state === 'sent') {
    const toAPerson = reason !== null && PEOPLE_ROUTED_REASONS.includes(reason);
    return (
      <PageFrame narrow className="gps-journey-root">
        <Title>Thank you</Title>
        <Help>That genuinely helps. You can close this tab and go back to your plan whenever you're ready.</Help>
        {toAPerson ? (
          <Card>
            <p style={{ margin: '0 0 var(--gps-s-3)' }}>
              A clinician can talk this through with you — it's free, and there's no pressure to go ahead.
            </p>
            <Link to={`/${clinicSlug}/callback`} style={{ color: 'var(--gps-link)', fontWeight: 600 }}>
              Request a free callback
            </Link>
          </Card>
        ) : null}
      </PageFrame>
    );
  }

  return (
    <PageFrame narrow className="gps-journey-root">
      <Title>What's holding you back?</Title>
      <Help style={{ marginBottom: 'var(--gps-s-5)' }}>
        One tap, and it's completely optional. It helps us get this right for people like you.
      </Help>
      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend className="gps-sr-only">Choose the reason that fits best</legend>
            <div style={{ display: 'grid', gap: 'var(--gps-s-2)' }}>
              {DROP_OFF_REASON_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--gps-s-3)',
                    minHeight: '44px',
                    padding: '0 var(--gps-s-3)',
                    border: '1px solid var(--gps-border)',
                    borderRadius: 'var(--gps-radius, 8px)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="drop-off-reason"
                    value={option.key}
                    checked={reason === option.key}
                    onChange={() => setReason(option.key)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {state === 'error' ? (
            <Alert tone="danger" role="alert" style={{ marginTop: 'var(--gps-s-4)' }}>
              We couldn't send that — please try again.
            </Alert>
          ) : null}
          <div style={{ marginTop: 'var(--gps-s-5)' }}>
            <JourneyButton type="submit" disabled={!reason || state === 'sending'}>
              {state === 'sending' ? 'Sending…' : 'Send'}
            </JourneyButton>
          </div>
        </form>
      </Card>
    </PageFrame>
  );
}
